import * as React from 'react';
import {
  Plugins,
  Component,
  Expressions,
  TypeImplementation,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  EElement,
  ERecord,
  EText,
  EEnum,
  EBoolean,
  EList,
  TListOfElement,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {
  qualify,
  namesEqual,
  type Qualified,
} from '@packages/systems/wfdl-Qualified';
import {getSecond} from '@packages/utilities/Pair';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {getTabContentTexts} from '@packages/utilities/platform';
import {concat} from '@packages/utilities/fp/utils';
import {generateGuid} from '@packages/utilities/guid';
import * as StringUtils from '@packages/utilities/string-utils';
import {AddIcon, IconButton, Tooltip} from '@packages/systems/spring';
import {CHILDREN_PATH} from '../constants';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {selectIsOnSecondaryLocale} from '@packages/systems/localization';
import {useTranslation} from '@packages/systems/designer-translations';

const TABS_MENU_TYPE = qualify(
  Plugins.name('Tabs'),
  Component.name('TabsMenu')
);
const TABS_CONTENT_TYPE = qualify(
  Plugins.name('Tabs'),
  Component.name('TabsContent')
);

const createTabPaneExpr = (label: string, id: string) =>
  getSecond(
    instantiateExpressionWithUniqueIds(
      EElement({
        id: 'Tabs Pane',
        type: qualify(Plugins.name('Tabs'), Component.name('TabsPane')),
        data: ERecord({
          attr: ERecord({
            'data-w-tab': EText(label),
          }),
          children: EList([]),
          tabs: ERecord({
            type: EText('pane'),
          }),
          tag: EEnum('div'),
        }),
      })
    )(id)
  );

const createTabLinkExpr = (label: string, id: string) =>
  getSecond(
    instantiateExpressionWithUniqueIds(
      EElement({
        id: 'Tabs Link',
        type: qualify(Plugins.name('Tabs'), Component.name('TabsLink')),
        data: ERecord({
          block: EText('inline'),
          tabs: ERecord({
            type: EText('link'),
          }),
          attr: ERecord({
            'data-w-tab': EText(label),
          }),
          children: EList([
            EElement({
              id: 'Block',
              type: qualify(Plugins.name('Basic'), Component.name('Block')),
              data: ERecord({
                tag: EEnum('div'),
                text: EBoolean(true),
                children: EList([
                  EElement({
                    id: 'String',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText(label),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      })
    )(id)
  );

const findElementExprIndexOfType = (
  exprList: Expression[],
  typeName: Qualified<Component.ComponentName>
) => {
  return exprList.findIndex((expr) => {
    const element = Expressions.getElement(expr);
    if (!element) return false;
    return namesEqual(element.type, typeName);
  });
};

const isOnSecondaryLocaleSelector = (state: DesignerStores) =>
  selectIsOnSecondaryLocale(state.LocalizationStore);

const AddTabRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const t = useTranslation();
  // Tree structure changes are not allowed in secondary locales
  const isOnSecondaryLocale = useDesignerSelector(isOnSecondaryLocaleSelector);

  const addTab = () => {
    const exprList = Expressions.getList(stripAll(expr)) || [];
    const tabsMenuExprIndex = findElementExprIndexOfType(
      exprList,
      TABS_MENU_TYPE
    );

    const tabsContentExprIndex = findElementExprIndexOfType(
      exprList,
      TABS_CONTENT_TYPE
    );

    // Both Tabs Menu & Tabs Content elements are needed to add tabs.
    // Unfortunately, we're currently allowing users to remove one or the other,
    // if this is the case, we'll no-op (do nothing).
    if (tabsMenuExprIndex === -1 || tabsContentExprIndex === -1) return;

    const tabsMenu = exprList[tabsMenuExprIndex];
    const tabsContent = exprList[tabsContentExprIndex];

    if (!tabsMenu || !tabsContent)
      throw Error('Unable to get list from Tabs Expression');

    const tabsMenuElement = Expressions.getElement(tabsMenu);

    const tabContentElement = Expressions.getElement(tabsContent);
    if (!tabsMenuElement || !tabContentElement)
      throw Error(
        'Unable to getElement from Tabs Menu or Tabs Content expression'
      );
    const tabsMenuChildren = Expressions.getIn(
      Expressions.getElementData(tabsMenuElement),
      CHILDREN_PATH
    );
    const tabsContentChildren = Expressions.getIn(
      Expressions.getElementData(tabContentElement),
      CHILDREN_PATH
    );
    if (!tabsMenuChildren || !tabsContentChildren)
      throw Error(
        'Unable to getChildren from Tabs Menu or Tab Content expression element'
      );

    const tabsMenuChildrenList = Expressions.getList(tabsMenuChildren);
    const tabsContentChildrenList = Expressions.getList(tabsContentChildren);
    if (!tabsMenuChildrenList || !tabsContentChildrenList)
      throw Error(
        'Unable to getChildren from Tabs Menu or Tabs Content children list'
      );

    const otherTabContentLabels =
      Expressions.everything(concat)(getTabContentTexts)(tabsContentChildren);

    const newLabel = StringUtils.increment(otherTabContentLabels, 'Tab');
    const newTabLink = createTabLinkExpr(newLabel, generateGuid());
    const newTabPane = createTabPaneExpr(newLabel, generateGuid());

    const newTabsMenuList = Expressions.setIn(
      tabsMenu,
      [{in: 'Element'}, ...CHILDREN_PATH],
      EList([...tabsMenuChildrenList, newTabLink])
    );

    const newTabsContentList = Expressions.setIn(
      tabsContent,
      [{in: 'Element'}, ...CHILDREN_PATH],
      EList([...tabsContentChildrenList, newTabPane])
    );

    exprList[tabsMenuExprIndex] = newTabsMenuList;
    exprList[tabsContentExprIndex] = newTabsContentList;

    dispatch(updateExpressionAction(EList(exprList)));
  };

  return (
    <Tooltip
      asChild
      label={t('designer.localization.disabled.tooltip')}
      isDisabled={!isOnSecondaryLocale}
    >
      <IconButton
        Icon={AddIcon}
        label="Add tab"
        variant="ghost"
        isDisabled={isOnSecondaryLocale}
        data-automation-id="AddTabPair"
        onClick={addTab}
      />
    </Tooltip>
  );
};

export const AddTabType = TypeImplementation.Alias({
  type: TListOfElement,
  render: AddTabRender,
});
